import React, { useRef, useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, Upload, X, ImagePlus, CheckCircle2 } from 'lucide-react';
import { JOB_TYPES, TIMELINES } from '@/data/site';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WEB3FORMS_ACCESS_KEY =
  process.env.REACT_APP_WEB3FORMS_ACCESS_KEY || '499a97f1-5184-4d0e-942d-bb0eebfccd7a';
const NOTIFICATION_EMAIL = 'ohanas@ohanapropertyexperts.com';
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const MAX_FILES = 10;

export default function QuoteForm({ defaultJobType = '' }) {
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    property_address: '',
    job_type: defaultJobType,
    timeline: '',
    notes: '',
  });
  const [photos, setPhotos] = useState([]);
  const [hazmatOk, setHazmatOk] = useState(false);
  const [termsOk, setTermsOk] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleFiles = (newFiles) => {
    const incoming = Array.from(newFiles || []);
    const valid = [];
    for (const f of incoming) {
      if (f.size > MAX_FILE_SIZE) {
        toast.error(`${f.name} exceeds 5MB and was skipped.`);
        continue;
      }
      // Wrap each File in a stable record so removing/reordering preserves React identity.
      valid.push({
        id: `${f.name}-${f.size}-${f.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
        file: f,
      });
    }
    setPhotos((prev) => {
      const merged = [...prev, ...valid];
      if (merged.length > MAX_FILES) {
        toast.warning(`Only the first ${MAX_FILES} photos will be uploaded.`);
        return merged.slice(0, MAX_FILES);
      }
      return merged;
    });
  };

  const removePhoto = (id) => {
    setPhotos((prev) => prev.filter((p) => p.id !== id));
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
  };

  const sendToWeb3Forms = async () => {
    const fd = new FormData();
    fd.append('access_key', WEB3FORMS_ACCESS_KEY);
    fd.append('subject', `New Quote Request from ${form.name} (${form.job_type})`);
    fd.append('from_name', 'Ohana Property Services Website');
    fd.append('botcheck', '');
    fd.append('replyto', form.email);
    fd.append('name', form.name);
    fd.append('phone', form.phone);
    fd.append('email', form.email);
    fd.append('property_address', form.property_address || '(not provided)');
    fd.append('job_type', form.job_type);
    fd.append('timeline', form.timeline);
    fd.append('notes', form.notes || '(none)');
    fd.append('hazmat_acknowledged', hazmatOk ? 'yes' : 'no');
    fd.append('terms_agreed', termsOk ? 'yes' : 'no');
    fd.append('sms_consent', smsConsent ? 'yes' : 'no');
    fd.append(
      'message',
      [
        `New Quote Request for Ohana Property Services`,
        ``,
        `Name: ${form.name}`,
        `Phone: ${form.phone}`,
        `Email: ${form.email}`,
        `Property Address: ${form.property_address || '(not provided)'}`,
        `Job Type: ${form.job_type}`,
        `Timeline: ${form.timeline}`,
        `Notes: ${form.notes || '(none)'}`,
        `Hazmat acknowledgment: ${hazmatOk ? 'YES, confirmed no prohibited items' : 'NO, not confirmed'}`,
        `Terms of Service agreement: ${termsOk ? 'YES, agreed' : 'NO, not agreed'}`,
        `SMS consent (10DLC): ${smsConsent ? 'YES, opted in' : 'NO, did not opt in'}`,
        `Photos attached: ${photos.length}`,
        `Notification destination: ${NOTIFICATION_EMAIL}`,
      ].join('\n'),
    );
    photos.forEach((p, i) => {
      const field = i === 0 ? 'attachment' : `attachment_${i}`;
      fd.append(field, p.file, p.file.name);
    });

    const resp = await fetch('https://api.web3forms.com/submit', {
      method: 'POST',
      body: fd,
    });
    let payload = {};
    try {
      payload = await resp.json();
    } catch (_) {
      payload = {};
    }
    return {
      ok: resp.ok && payload.success === true,
      message: payload.message || (resp.ok ? 'Forwarded' : `HTTP ${resp.status}`),
    };
  };

  const sendToBackend = async (web3formsStatus, web3formsMessage) => {
    const fd = new FormData();
    fd.append('name', form.name);
    fd.append('phone', form.phone);
    fd.append('email', form.email);
    fd.append('property_address', form.property_address || '');
    fd.append('job_type', form.job_type);
    fd.append('timeline', form.timeline);
    fd.append('notes', form.notes || '');
    fd.append('hazmat_acknowledged', hazmatOk ? 'yes' : 'no');
    fd.append('terms_agreed', termsOk ? 'yes' : 'no');
    fd.append('sms_consent', smsConsent ? 'yes' : 'no');
    fd.append('web3forms_status', web3formsStatus);
    fd.append('web3forms_message', web3formsMessage);
    fd.append('photo_count', String(photos.length));
    fd.append('photo_filenames_csv', photos.map((p) => p.file.name).join(','));
    const resp = await fetch(`${API}/quote`, { method: 'POST', body: fd });
    if (!resp.ok) {
      const data = await resp.json().catch(() => ({}));
      throw new Error(data?.detail || 'Backend save failed');
    }
    return resp.json();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.name.trim() || !form.phone.trim()) {
      toast.error('Please fill in your name and phone.');
      return;
    }
    if (!hazmatOk) {
      toast.error('Please confirm your items comply with the Ohana Acceptance Policy.');
      return;
    }
    if (!smsConsent) {
      toast.error('Please consent to receive SMS messages so we can text you back.');
      return;
    }

    setSubmitting(true);
    let w3 = { ok: false, message: 'not attempted' };
    try {
      // 1) forward email + attachments via Web3Forms (client-side)
      try {
        w3 = await sendToWeb3Forms();
      } catch (err) {
        w3 = { ok: false, message: err?.message || 'web3forms error' };
      }

      // 2) persist to backend MongoDB regardless of email outcome
      await sendToBackend(w3.ok ? 'sent' : 'failed', w3.message);

      setSuccess(true);
      if (w3.ok) {
        toast.success('Thanks! Your request was sent. We will reply with a quote shortly.');
      } else {
        toast.success(
          'Thanks! We saved your request. If email is delayed, we will follow up directly.',
        );
      }
      setForm({
        name: '',
        phone: '',
        email: '',
        property_address: '',
        job_type: '',
        timeline: '',
        notes: '',
      });
      setPhotos([]);
      setHazmatOk(false);
      setTermsOk(false);
      setSmsConsent(false);
    } catch (err) {
      toast.error(err.message || 'Something went wrong. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div
        className="rounded-2xl border-2 border-ohana-accent bg-white p-10 text-center"
        data-testid="quote-success"
      >
        <span className="inline-grid h-16 w-16 place-items-center rounded-full bg-ohana-accent/10 text-ohana-accent">
          <CheckCircle2 className="h-8 w-8" />
        </span>
        <h3 className="mt-5 font-serif text-3xl text-ohana-primary">Request received.</h3>
        <p className="mt-3 text-ohana-text-muted max-w-md mx-auto">
          We'll review your photos and reply with a transparent quote, usually the same day. Check your inbox.
        </p>
        <button
          type="button"
          onClick={() => setSuccess(false)}
          className="mt-6 text-ohana-primary font-semibold underline decoration-ohana-accent decoration-2 underline-offset-4"
          data-testid="quote-submit-another"
        >
          Submit another request
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="grid grid-cols-1 md:grid-cols-2 gap-5"
      data-testid="quote-form"
      noValidate
    >
      <div className="md:col-span-1">
        <label className="text-xs font-bold uppercase tracking-wider text-ohana-text-muted">Name *</label>
        <input
          type="text"
          name="name"
          value={form.name}
          onChange={handleChange}
          required
          autoComplete="name"
          className="input-default mt-2"
          placeholder="Your full name"
          data-testid="quote-input-name"
        />
      </div>

      <div className="md:col-span-1">
        <label className="text-xs font-bold uppercase tracking-wider text-ohana-text-muted">Phone *</label>
        <input
          type="tel"
          name="phone"
          value={form.phone}
          onChange={handleChange}
          required
          autoComplete="tel"
          className="input-default mt-2"
          placeholder="(555) 555-5555"
          data-testid="quote-input-phone"
        />
      </div>

      <div className="md:col-span-2">
        <label className="text-xs font-bold uppercase tracking-wider text-ohana-text-muted">Email</label>
        <input
          type="email"
          name="email"
          value={form.email}
          onChange={handleChange}
          autoComplete="email"
          className="input-default mt-2"
          placeholder="you@email.com (optional)"
          data-testid="quote-input-email"
        />
      </div>

      <div className="md:col-span-2">
        <label className="text-xs font-bold uppercase tracking-wider text-ohana-text-muted">Property Address</label>
        <input
          type="text"
          name="property_address"
          value={form.property_address}
          onChange={handleChange}
          className="input-default mt-2"
          placeholder="Street, City, IN ZIP"
          data-testid="quote-input-address"
        />
      </div>

      <div className="md:col-span-1">
        <label className="text-xs font-bold uppercase tracking-wider text-ohana-text-muted">Job Type</label>
        <Select
          value={form.job_type}
          onValueChange={(v) => setForm((p) => ({ ...p, job_type: v }))}
        >
          <SelectTrigger
            className="input-default mt-2 [&>span]:text-ohana-text"
            data-testid="quote-select-job-type"
          >
            <SelectValue placeholder="Select a service (optional)" />
          </SelectTrigger>
          <SelectContent>
            {JOB_TYPES.map((t) => (
              <SelectItem key={t} value={t} data-testid={`job-type-${t.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="md:col-span-1">
        <label className="text-xs font-bold uppercase tracking-wider text-ohana-text-muted">Timeline</label>
        <Select
          value={form.timeline}
          onValueChange={(v) => setForm((p) => ({ ...p, timeline: v }))}
        >
          <SelectTrigger
            className="input-default mt-2 [&>span]:text-ohana-text"
            data-testid="quote-select-timeline"
          >
            <SelectValue placeholder="When do you need it done? (optional)" />
          </SelectTrigger>
          <SelectContent>
            {TIMELINES.map((t) => (
              <SelectItem key={t} value={t} data-testid={`timeline-${t.toLowerCase().replace(/[^a-z]+/g, '-').slice(0, 30)}`}>
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="md:col-span-2">
        <label className="text-xs font-bold uppercase tracking-wider text-ohana-text-muted">Notes</label>
        <textarea
          name="notes"
          value={form.notes}
          onChange={handleChange}
          rows={4}
          maxLength={1000}
          className="input-default mt-2 resize-none"
          placeholder="Anything we should know? Access, stairs, timeline constraints, etc."
          data-testid="quote-input-notes"
        />
      </div>

      <div className="md:col-span-2">
        <label className="text-xs font-bold uppercase tracking-wider text-ohana-text-muted">
          Photos (optional, recommended for most accurate quote)
        </label>
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          className="mt-2 rounded-xl border-2 border-dashed border-ohana-border bg-white px-6 py-8 text-center transition hover:border-ohana-accent"
          data-testid="quote-photo-dropzone"
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,.pdf"
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="quote-input-photos"
          />
          <span className="inline-grid h-12 w-12 place-items-center rounded-full bg-ohana-accent/10 text-ohana-accent">
            <ImagePlus className="h-5 w-5" />
          </span>
          <p className="mt-3 text-sm text-ohana-text">
            Drag photos here, or{' '}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="font-bold text-ohana-primary underline decoration-ohana-accent underline-offset-4"
              data-testid="quote-photo-browse"
            >
              browse
            </button>
            .
          </p>
          <p className="mt-1 text-xs text-ohana-text-muted">JPG, PNG, HEIC, or PDF · 5MB max each</p>
        </div>

        {photos.length > 0 && (
          <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="quote-photo-list">
            {photos.map((p) => (
              <li
                key={p.id}
                className="flex items-center justify-between rounded-lg border border-ohana-border bg-white px-3 py-2 text-sm"
              >
                <span className="truncate text-ohana-text">{p.file.name}</span>
                <button
                  type="button"
                  onClick={() => removePhoto(p.id)}
                  className="ml-3 grid h-7 w-7 place-items-center rounded-full bg-ohana-surface text-ohana-text-muted hover:bg-ohana-primary hover:text-white"
                  aria-label={`Remove ${p.file.name}`}
                  data-testid={`quote-photo-remove-${p.id}`}
                >
                  <X className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="md:col-span-2">
        <label
          className={`flex items-start gap-3 rounded-xl border bg-white px-4 py-4 cursor-pointer transition ${
            hazmatOk
              ? 'border-ohana-accent ring-2 ring-ohana-accent/20'
              : 'border-ohana-border hover:border-ohana-accent/60'
          }`}
          data-testid="quote-hazmat-acknowledgment"
        >
          <input
            type="checkbox"
            checked={hazmatOk}
            onChange={(e) => setHazmatOk(e.target.checked)}
            className="mt-1 h-5 w-5 flex-none rounded border-ohana-border text-ohana-accent focus:ring-2 focus:ring-ohana-accent/30 cursor-pointer"
            data-testid="quote-hazmat-checkbox"
            required
          />
          <span className="text-sm text-ohana-text leading-relaxed">
            I confirm my items comply with the Ohana Acceptance Policy. I have read and understand what Ohana accepts and does not accept.{' '}
            <a
              href="#cannot-take"
              onClick={(e) => {
                e.preventDefault();
                document
                  .querySelector('[data-testid="cannot-take-section"]')
                  ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              className="text-ohana-primary font-semibold underline decoration-ohana-accent decoration-2 underline-offset-4"
              data-testid="quote-hazmat-list-link"
            >
              Read the full acceptance policy.
            </a>
          </span>
        </label>
      </div>

      <div className="md:col-span-2">
        <label
          className={`flex items-start gap-3 rounded-xl border bg-white px-4 py-4 cursor-pointer transition ${
            smsConsent
              ? 'border-ohana-accent ring-2 ring-ohana-accent/20'
              : 'border-ohana-border hover:border-ohana-accent/60'
          }`}
          data-testid="quote-sms-consent-acknowledgment"
        >
          <input
            type="checkbox"
            checked={smsConsent}
            onChange={(e) => setSmsConsent(e.target.checked)}
            className="mt-1 h-5 w-5 flex-none rounded border-ohana-border text-ohana-accent focus:ring-2 focus:ring-ohana-accent/30 cursor-pointer"
            data-testid="quote-sms-consent-checkbox"
            required
          />
          <span className="text-sm text-ohana-text leading-relaxed">
            By providing your phone number, you consent to receive SMS messages from Ohana Property &amp; Transition Services LLC regarding your inquiry. Message and data rates may apply. Reply STOP to opt out.
          </span>
        </label>
      </div>

      <div className="md:col-span-2">
        <label
          className={`flex items-start gap-3 rounded-xl border bg-white px-4 py-4 cursor-pointer transition ${
            termsOk
              ? 'border-ohana-accent ring-2 ring-ohana-accent/20'
              : 'border-ohana-border hover:border-ohana-accent/60'
          }`}
          data-testid="quote-terms-acknowledgment"
        >
          <input
            type="checkbox"
            checked={termsOk}
            onChange={(e) => setTermsOk(e.target.checked)}
            className="mt-1 h-5 w-5 flex-none rounded border-ohana-border text-ohana-accent focus:ring-2 focus:ring-ohana-accent/30 cursor-pointer"
            data-testid="quote-terms-checkbox"
          />
          <span className="text-sm text-ohana-text leading-relaxed">
            I agree to the{' '}
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-ohana-primary font-semibold underline decoration-ohana-accent decoration-2 underline-offset-4"
              data-testid="quote-terms-link"
            >
              Terms of Service
            </a>
            .
          </span>
        </label>
      </div>

      <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
        <p className="text-xs text-ohana-text-muted max-w-md">
          By submitting, you agree we may contact you about your project. Your info is never sold.
        </p>
        <button
          type="submit"
          disabled={submitting}
          className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="quote-submit"
        >
          {submitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Upload className="mr-2 h-4 w-4" />
              Get My Free Quote
            </>
          )}
        </button>
      </div>

      <div className="md:col-span-2">
        <p
          className="rounded-xl bg-ohana-surface border border-ohana-border px-4 py-3.5 text-sm text-ohana-text leading-relaxed"
          data-testid="quote-sms-followup"
        >
          <span className="font-semibold text-ohana-primary">After submitting,</span> we&apos;ll text you within 2 hours. Reply to that text with photos of your property for the most accurate quote.
        </p>
      </div>
    </form>
  );
}
